import { Loader2, Save, RotateCcw, Check, X, Database, Clock, AlertTriangle, ArrowRight } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useState } from "react";
import { Multiplier, UpdateMultiplierRequest, updateMultiplier, recalculateDatabase } from "@/api/multipliers";

interface SettingsHeaderProps {
    isLoading: boolean;
    hasChanges: boolean;
    onRefresh: () => void;
    multipliers: Multiplier[];
    editedMultipliers: Map<number, Partial<Multiplier>>;
    setEditedMultipliers: (map: Map<number, Partial<Multiplier>>) => void;
}

export function SettingsHeader({
    hasChanges,
    onRefresh,
    multipliers,
    editedMultipliers,
    setEditedMultipliers,
}: SettingsHeaderProps) {
    const [showRecalculateModal, setShowRecalculateModal] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const queryClient = useQueryClient();

    const updateMultiplierMutation = useMutation({
        mutationFn: async (updates: { id: number; data: UpdateMultiplierRequest }[]) => {
            await Promise.all(updates.map(({ id, data }) => updateMultiplier(id, data)));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["multipliers"] });
            setEditedMultipliers(new Map());
            setShowSaveModal(false);
            toast.success("Settings saved successfully!", {
                icon: <Check className="w-4 h-4 text-general-green" />,
                description: "To apply the changes, recalculate the database",
            });
        },
        onError: (error: Error) => {
            toast.error(error.message || "Failed to save changes", {
                icon: <X className="w-4 h-4 text-general-red" />,
            });
        },
    });

    const recalculateMutation = useMutation({
        mutationFn: recalculateDatabase,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["multipliers"] });
            setShowRecalculateModal(false);
            toast.success("Database recalculation completed!", {
                icon: <Check className="w-4 h-4 text-general-green" />,
            });
        },
        onError: (error: Error) => {
            setShowRecalculateModal(false);
            toast.error(error.message || "Failed to recalculate database", {
                icon: <X className="w-4 h-4 text-general-red" />,
            });
        },
    });

    const saveChanges = async () => {
        const updates = Array.from(editedMultipliers.entries())
            .map(([id, changes]) => {
                const multiplier = multipliers.find((m) => m.id === id);
                if (!multiplier) return null;

                const updateData: UpdateMultiplierRequest = {
                    isActive: changes.isActive !== undefined ? changes.isActive : multiplier.isActive,
                };

                if (changes.weight !== undefined) {
                    updateData.weight = changes.weight;
                }

                if (changes.maxPoints !== undefined) {
                    updateData.maxPoints = changes.maxPoints;
                }

                return { id, data: updateData };
            })
            .filter(Boolean) as { id: number; data: UpdateMultiplierRequest }[];

        await updateMultiplierMutation.mutateAsync(updates);
    };

    const handleRecalculateClick = () => {
        setShowRecalculateModal(true);
    };

    const handleConfirmRecalculate = () => {
        recalculateMutation.mutate();
    };

    const handleCancelRecalculate = () => {
        setShowRecalculateModal(false);
    };

    const formatStatName = (name: string): string =>
        name
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

    const changeSummary = multipliers
        .map((m) => {
            const edited = editedMultipliers.get(m.id);
            if (!edited) return null;

            const fields: { label: string; from: string; to: string }[] = [];

            if (edited.weight !== undefined && edited.weight !== m.weight) {
                fields.push({ label: "Weight", from: `${m.weight ?? "\u2014"}`, to: `${edited.weight ?? "\u2014"}` });
            }
            if (edited.maxPoints !== undefined && edited.maxPoints !== m.maxPoints) {
                fields.push({
                    label: "Max Points",
                    from: `${m.maxPoints ?? "\u2014"}`,
                    to: `${edited.maxPoints ?? "\u2014"}`,
                });
            }
            if (edited.isActive !== undefined && edited.isActive !== m.isActive) {
                fields.push({
                    label: "Status",
                    from: m.isActive ? "Active" : "Inactive",
                    to: edited.isActive ? "Active" : "Inactive",
                });
            }

            if (fields.length === 0) return null;
            return { id: m.id, name: formatStatName(m.statName), fields };
        })
        .filter(Boolean) as { id: number; name: string; fields: { label: string; from: string; to: string }[] }[];

    const handleSaveClick = () => {
        if (!hasChanges) return;
        setShowSaveModal(true);
    };

    const handleConfirmSave = () => {
        saveChanges();
    };

    const handleCancelSave = () => {
        setShowSaveModal(false);
    };

    return (
        <>
            <div
                id="onboarding-settings-header"
                className="flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-6 mb-4 sm:mb-6"
            >
                <div>
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-primary tracking-tight mb-1 sm:mb-2 font-headline">
                        Multiplier Settings
                    </h1>
                    <p className="text-sm sm:text-base text-on-surface-variant font-medium">
                        Configure weight and max points for different stat multipliers.
                    </p>
                </div>
                <div id="onboarding-settings-header-buttons" className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <button
                        onClick={onRefresh}
                        className="flex-1 md:flex-none justify-center whitespace-nowrap bg-general-orange text-white px-3 sm:px-4 py-2 rounded-md font-headline font-bold uppercase tracking-wider text-xs hover:opacity-90 transition-all flex items-center gap-2 cursor-pointer"
                        disabled={updateMultiplierMutation.isPending || recalculateMutation.isPending}
                    >
                        <RotateCcw className="w-4 h-4" />
                        Refresh
                    </button>
                    <button
                        onClick={handleRecalculateClick}
                        className="flex-1 md:flex-none justify-center whitespace-nowrap bg-general-purple text-white px-3 sm:px-4 py-2 rounded-md font-headline font-bold uppercase tracking-wider text-xs hover:opacity-90 transition-all flex items-center gap-2 cursor-pointer"
                        disabled={updateMultiplierMutation.isPending || recalculateMutation.isPending}
                    >
                        <Database className="w-4 h-4" />
                        <span className="hidden sm:inline">Recalculate Database</span>
                        <span className="sm:hidden">Recalc</span>
                    </button>
                    <button
                        onClick={handleSaveClick}
                        disabled={!hasChanges || updateMultiplierMutation.isPending || recalculateMutation.isPending}
                        className={`flex-1 md:flex-none justify-center whitespace-nowrap cursor-pointer px-3 sm:px-4 py-2 rounded-md font-headline font-bold uppercase tracking-wider text-xs transition-all flex items-center gap-2 ${
                            hasChanges && !updateMultiplierMutation.isPending && !recalculateMutation.isPending
                                ? "bg-general-blue text-white hover:opacity-90"
                                : "bg-surface-container-high text-on-surface-variant cursor-not-allowed"
                        }`}
                    >
                        {updateMultiplierMutation.isPending ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                <span className="hidden sm:inline">Save Changes</span>
                                <span className="sm:hidden">Save</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {showRecalculateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                    <div className="bg-surface-container-lowest rounded-md shadow-[0_30px_60px_rgba(5,0,102,0.10)] w-full max-w-md mx-4 overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-6 border-b border-outline-variant/20 flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <Database className="w-6 h-6 text-general-purple" />
                                <h2 className="text-xl font-headline font-bold text-on-surface">
                                    Recalculate Database
                                </h2>
                            </div>
                            <button
                                onClick={handleCancelRecalculate}
                                className="p-2 text-on-surface-variant hover:text-on-surface rounded-md hover:bg-surface-container transition-colors cursor-pointer"
                                disabled={recalculateMutation.isPending}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="space-y-4">
                                <p className="text-on-surface text-sm leading-relaxed">
                                    This will recalculate all KPIs and database statistics based on current multiplier
                                    settings.
                                </p>
                                <div className="bg-surface-container rounded-md p-4 space-y-2">
                                    <div className="flex items-center gap-2 text-sm text-on-surface">
                                        <Clock className="w-4 h-4 text-general-orange" />
                                        <span>Process may take around 45 seconds</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-on-surface">
                                        <AlertTriangle className="w-4 h-4 text-general-orange" />
                                        <span>Some functionalities may be temporarily unavailable</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-on-surface">
                                        <Database className="w-4 h-4 text-general-orange" />
                                        <span>All statistics will be updated to reflect current settings</span>
                                    </div>
                                </div>
                                <div className="bg-general-orange-background border border-general-orange/30 rounded-md p-3">
                                    <p className="text-general-orange text-sm font-medium">
                                        ⚠️ Please do not navigate away during this process
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t border-outline-variant/20 bg-surface-container-low flex-shrink-0">
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    onClick={handleCancelRecalculate}
                                    disabled={recalculateMutation.isPending}
                                    className="px-4 py-2 text-on-surface-variant hover:text-on-surface border border-outline-variant hover:border-on-surface-variant rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-headline font-bold uppercase tracking-wider text-xs"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmRecalculate}
                                    disabled={recalculateMutation.isPending}
                                    className="bg-general-purple text-white px-4 py-2 rounded-md font-headline font-bold uppercase tracking-wider text-xs hover:opacity-90 transition-all flex items-center gap-2 cursor-pointer disabled:bg-surface-container-high disabled:text-on-surface-variant disabled:cursor-not-allowed"
                                >
                                    {recalculateMutation.isPending ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Recalculating...
                                        </>
                                    ) : (
                                        <>
                                            <Database className="w-4 h-4" />
                                            Start Recalculation
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showSaveModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
                    <div className="bg-surface-container-lowest rounded-md shadow-[0_30px_60px_rgba(5,0,102,0.10)] w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-outline-variant/20 shrink-0">
                            <div className="flex items-center gap-3">
                                <Save className="w-5 h-5 sm:w-6 sm:h-6 text-general-blue" />
                                <h2 className="text-lg sm:text-xl font-headline font-bold text-on-surface">
                                    Confirm Changes
                                </h2>
                            </div>
                            <button
                                onClick={handleCancelSave}
                                className="p-2 text-on-surface-variant hover:text-on-surface rounded-md hover:bg-surface-container transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={updateMultiplierMutation.isPending}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-4 sm:p-6 overflow-auto flex-1">
                            <p className="text-on-surface text-sm leading-relaxed mb-4">
                                You are about to save the following{" "}
                                <span className="font-semibold">{changeSummary.length}</span> change
                                {changeSummary.length !== 1 ? "s" : ""}:
                            </p>
                            <div className="space-y-3">
                                {changeSummary.map((item) => (
                                    <div
                                        key={item.id}
                                        className="bg-surface-container-low border border-outline-variant/30 rounded-md p-3"
                                    >
                                        <h3 className="text-sm font-semibold text-on-surface mb-2">{item.name}</h3>
                                        <div className="space-y-1.5">
                                            {item.fields.map((field) => (
                                                <div
                                                    key={field.label}
                                                    className="flex items-center justify-between gap-2 text-xs"
                                                >
                                                    <span className="text-on-surface-variant">{field.label}</span>
                                                    <div className="flex items-center gap-1.5 font-medium">
                                                        <span className="text-on-surface-variant line-through">
                                                            {field.from}
                                                        </span>
                                                        <ArrowRight className="w-3 h-3 text-general-blue shrink-0" />
                                                        <span className="text-general-blue">{field.to}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="bg-general-blue-background border border-general-blue/30 rounded-md p-3 mt-4">
                                <p className="text-general-blue text-sm font-medium">
                                    After saving, recalculate the database to apply these changes.
                                </p>
                            </div>
                        </div>

                        <div className="px-4 sm:px-6 py-4 border-t border-outline-variant/20 bg-surface-container-low shrink-0">
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    onClick={handleCancelSave}
                                    disabled={updateMultiplierMutation.isPending}
                                    className="px-4 py-2 text-on-surface-variant hover:text-on-surface border border-outline-variant hover:border-on-surface-variant rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-headline font-bold uppercase tracking-wider text-xs"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmSave}
                                    disabled={updateMultiplierMutation.isPending}
                                    className="bg-general-blue text-white px-4 py-2 rounded-md font-headline font-bold uppercase tracking-wider text-xs hover:opacity-90 transition-all flex items-center gap-2 cursor-pointer disabled:bg-surface-container-high disabled:text-on-surface-variant disabled:cursor-not-allowed"
                                >
                                    {updateMultiplierMutation.isPending ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            Confirm & Save
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
